import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext';
import { verifyIdentity } from '../services/identityService';
import { IDENTITY_ERROR_MESSAGES } from '../constants/appConfig';
import type { IdentityRequest, IdentityErrorCode } from '../types/api.types';

interface VerificationState {
  isLoading:         boolean;
  errorCode:         IdentityErrorCode | null;
  errorMessage:      string | null;
  attemptsRemaining: number | null;
}

export function useIdentityVerification() {
  const navigate                          = useNavigate();
  const { jwtToken, setIdentityVerified, formAccessId } = useSession();

  const [state, setState] = useState<VerificationState>({
    isLoading:         false,
    errorCode:         null,
    errorMessage:      null,
    attemptsRemaining: null,
  });

  async function submit(
    payload:          IdentityRequest,
    onCaptchaRefresh: (captchaId: string, imageBase64: string) => void
  ) {
    try {
     /*  console.log('🔥 useIdentityVerification.submit() called');
      console.log('  Payload:', payload);
      console.log('  JWT Token:', jwtToken?.substring(0, 20) + '...'); */

      if (!jwtToken) {
        /* console.log('❌ No JWT token - redirecting to error'); */
        navigate('/error?reason=NO_TOKEN_PROVIDED', { replace: true });
        return;
      }

      /* console.log('⏳ Setting loading state and calling API...'); */
      setState({ isLoading: true, errorCode: null, errorMessage: null, attemptsRemaining: null });

      const result = await verifyIdentity(payload, jwtToken, formAccessId);
      /* console.log('📨 API response received:', result); */

      if (result.success) {
       /*  console.log('🎉 Identity verification successful!');
        console.log('  SessionToken received:', result.sessionToken);
        console.log('  Token length:', result.sessionToken?.length);
        console.log('  Is valid JWT format?', result.sessionToken?.split('.').length === 3 ? 'YES' : 'NO');
 */
        setIdentityVerified(true, result.sessionToken);
        navigate('/form-access', { replace: true });
        return;
      }

    /*   console.log('❌ Identity verification failed:', result.errorCode); */

      // Load the new CAPTCHA returned by the server (avoids an extra GET round-trip)
      if (result.newCaptchaId && result.newImageBase64) {
        /* console.log('🔄 Refreshing CAPTCHA'); */
        onCaptchaRefresh(result.newCaptchaId, result.newImageBase64);
      }

      if (result.errorCode === 'TOKEN_INACTIVE') {
        navigate('/error?reason=TOKEN_INACTIVE', { replace: true });
        return;
      }

      if (result.errorCode === 'RATE_LIMIT_EXCEEDED') {
        setState({
          isLoading:         false,
          errorCode:         'RATE_LIMIT_EXCEEDED',
          errorMessage:      IDENTITY_ERROR_MESSAGES['RATE_LIMIT_EXCEEDED'],
          attemptsRemaining: null,
        });
        return;
      }

      if (result.errorCode === 'ATTEMPT_FAILURE') {
        setState({
          isLoading:         false,
          errorCode:         'ATTEMPT_FAILURE',
          errorMessage:      IDENTITY_ERROR_MESSAGES['ATTEMPT_FAILURE'],
          attemptsRemaining: null,
        });
        return;
      }

      const attemptsRemaining = result.attemptsRemaining ?? null;
      let errorMessage =
        IDENTITY_ERROR_MESSAGES[result.errorCode] ?? IDENTITY_ERROR_MESSAGES['UNKNOWN_ERROR'];

      if (result.errorCode === 'IDENTITY_MISMATCH' && attemptsRemaining != null) {
        errorMessage += ` Attempts remaining: ${attemptsRemaining}`;
      }

      setState({
        isLoading:         false,
        errorCode:         result.errorCode,
        errorMessage,
        attemptsRemaining,
      });
    } catch (error) {
      /* console.error('❌ Error in submit function:', error); */
      setState({
        isLoading: false,
        errorCode: 'UNKNOWN_ERROR',
        errorMessage: 'An unexpected error occurred. Please try again.',
        attemptsRemaining: null,
      });
    }
  }

  return { ...state, submit };
}
